import { addDays, parseIso, periodFor, todayIso, type BudgetPeriod, type PlannedEvent, type Transaction } from '@fin/shared';

import { TxRow, dedupeTransfers } from '../transactions/public.ts';

import { Icon, Tile } from '../../components/ui.tsx';
import { authStore, qs } from '../../lib/api.ts';

import { dateLong, money, plural } from '../../lib/format.ts';


import { useQuery } from '../../lib/query.ts';
import { Link } from '../../lib/router.tsx';

export function PeriodOps({ period, onOpen }: { period: BudgetPeriod; onOpen: (t: Transaction) => void }) {
  const tx = useQuery<{ items: Transaction[] }>(`/transactions${qs({ from: period.startDate, to: period.endDate, limit: 1000 })}`);
  const list = dedupeTransfers(tx.data?.items ?? []).slice(0, 8);
  const today = todayIso();
  return (
    <section className="section" aria-labelledby="period-ops">
      <div className="section-head">
        <h2 id="period-ops">Операции</h2>
        <Link to={`/transactions${qs({ from: period.startDate, to: period.endDate })}`} className="link section-aside">Все</Link>
      </div>
      {!tx.data ? <div className="skeleton sk-rows" /> : list.length ? (
        <ul className="tx-list">
          {list.map((t) => <TxRow key={t.id} t={t} showDate={t.date !== today} onClick={() => onOpen(t)} />)}
        </ul>
      ) : <p className="muted small">В этом периоде операций нет.</p>}
    </section>
  );
}

export function UpcomingEvents({ period }: { period: BudgetPeriod }) {
  const q = useQuery<PlannedEvent[]>('/events');
  const days = authStore.get().user?.periodStartDays ?? [10, 25];
  const next = periodFor(addDays(period.endDate, 1), days);
  const afterNext = periodFor(addDays(next.end, 1), days);
  const today = todayIso();
  const events = (q.data ?? []).filter((e) => !e.done && e.date >= today && e.date <= afterNext.end)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (!events.length) return null;
  const inDays = (iso: string) => Math.round((parseIso(iso).getTime() - parseIso(today).getTime()) / 86400000);
  const when = (n: number) => (n === 0 ? 'сегодня' : n === 1 ? 'завтра' : `через ${n} ${plural(n, 'день', 'дня', 'дней')}`);
  const where = (iso: string) => (iso <= period.endDate ? 'в этом периоде' : iso <= next.end ? 'в следующем периоде' : 'через период');
  return (
    <section className="upcoming" aria-labelledby="upcoming-events">
      <h2 id="upcoming-events" className="upcoming-title">
        <Icon name="calendar" size={18} /> {events[0].date === today ? 'Сегодня событие' : 'Скоро событие'}
      </h2>
      <ul className="upcoming-list">
        {events.map((e) => {
          const n = inDays(e.date);
          return (
            <li key={e.id} className="upcoming-row">
              <span className="upcoming-text">
                <strong>{e.title}</strong>
                <span className="muted small">{n === 0 ? 'сегодня' : `${dateLong(e.date)} · ${when(n)} · ${where(e.date)}`}</span>
              </span>
              {e.amount ? <span className="money">{money(e.amount, e.currency)}</span> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function PeriodEvents({ period }: { period: BudgetPeriod }) {
  const q = useQuery<PlannedEvent[]>('/events');
  const events = (q.data ?? []).filter((e) => !e.done && e.date >= period.startDate && e.date <= period.endDate);
  if (!events.length) return null;
  return (
    <section className="section" aria-labelledby="period-events">
      <div className="section-head"><h2 id="period-events">События</h2><Link to="/plan" className="link section-aside">Все</Link></div>
      <ul className="tx-list">
        {events.map((e) => (
          <li key={e.id} className="tx">
            <Tile icon="gift" color="#E8C36A" />
            <span className="tx-text"><span className="tx-title">{e.title}</span><span className="tx-sub">{dateLong(e.date)}</span></span>
            <span className="tx-right"><span className="money">{money(e.amount, e.currency)}</span></span>
          </li>
        ))}
      </ul>
    </section>
  );
}
